import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';

// TDMS Balance Types
// B = Borc (Debit normal balance)
// A = Alacak (Credit normal balance)
// C = Cift (Can be either)
const BALANCE_TYPES = [
  { value: 'B', label: 'B - Borc (Debit)' },
  { value: 'A', label: 'A - Alacak (Credit)' },
  { value: 'C', label: 'C - Cift (Both)' },
] as const;

type TdmsBalanceType = 'B' | 'A' | 'C';

const accountSchema = z.object({
  code: z.string().min(1, 'Account code is required'),
  name: z.string().min(1, 'Account name is required'),
  balanceType: z.enum(['B', 'A', 'C']),
  description: z.string().optional(),
  currency: z.string().default('TRY'),
});

type AccountFormData = z.infer<typeof accountSchema>;

interface TradingAccount {
  id: string;
  code: string;
  name: string;
  type: string;
  balanceType: string;
  parentCode?: string | null;
  description?: string | null;
  currency: string;
  isSystemAccount: boolean;
}

interface TradingAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account?: TradingAccount;
  onSuccess?: () => void;
}

export function TradingAccountModal({ open, onOpenChange, account, onSuccess }: TradingAccountModalProps) {
  const { t } = useTranslation('tradingAccounting');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const isEditing = !!account;

  // Preparation mode (development) unlocks code, para birimi and system
  // accounts; in production the chart is locked and this form behaves as before.
  const { data: chartPrep } = trpc.tradingAccount.chartPrepStatus.useQuery(undefined, {
    enabled: open,
  });
  const unlocked = chartPrep?.enabled === true;
  const systemLocked = !unlocked && account?.isSystemAccount === true;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<AccountFormData>({
    defaultValues: account
      ? {
          code: account.code,
          name: account.name,
          balanceType: account.balanceType as TdmsBalanceType,
          description: account.description ?? undefined,
          currency: account.currency,
        }
      : {
          balanceType: 'B',
          currency: 'TRY',
        },
  });

  const balanceType = watch('balanceType');

  // Reset form when account changes
  useEffect(() => {
    setWarnings([]);
    if (account) {
      reset({
        code: account.code,
        name: account.name,
        balanceType: account.balanceType as TdmsBalanceType,
        description: account.description ?? undefined,
        currency: account.currency,
      });
    } else {
      reset({
        balanceType: 'B',
        currency: 'TRY',
      });
    }
  }, [account, reset]);

  const utils = trpc.useUtils();

  const createMutation = trpc.tradingAccount.create.useMutation({
    onSuccess: () => {
      utils.tradingAccount.list.invalidate();
      utils.tradingAccount.getTree.invalidate();
      utils.tradingAccount.stats.invalidate();
      reset();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const updateMutation = trpc.tradingAccount.update.useMutation({
    onSuccess: (result) => {
      utils.tradingAccount.list.invalidate();
      utils.tradingAccount.getTree.invalidate();
      utils.tradingAccount.byId.invalidate();
      utils.tradingAccount.stats.invalidate();
      onSuccess?.();
      setIsSubmitting(false);
      // Saved either way; hold the dialog open when the change moved something
      // the reports or the posting catalog key off.
      const notes = result?.warnings ?? [];
      if (notes.length > 0) {
        setWarnings(notes);
        return;
      }
      onOpenChange(false);
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: AccountFormData) => {
    setIsSubmitting(true);

    if (isEditing && account) {
      setWarnings([]);
      updateMutation.mutate({
        id: account.id,
        // Only send code if it actually changed (avoids re-derivation & uniqueness check on no-op edits)
        ...(data.code !== account.code ? { code: data.code } : {}),
        name: data.name,
        description: data.description || null,
        balanceType: data.balanceType,
        // Preparation-mode only; the server refuses it on a locked chart.
        ...(unlocked && data.currency !== account.currency ? { currency: data.currency } : {}),
      });
    } else {
      createMutation.mutate({
        code: data.code,
        name: data.name,
        balanceType: data.balanceType,
        description: data.description,
        currency: data.currency,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-lg flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t('accountModal.editTitle', 'Edit Account')
              : t('accountModal.newTitle', 'New Account')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('accountModal.editDescription', 'Update the account information below.')
              : t('accountModal.newDescription', 'Create a new TDMS account. Account type will be derived from the code prefix.')}
          </DialogDescription>
          {unlocked && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {t(
                'accountModal.prepModeNotice',
                'Hesap planı hazırlık modu açık: kod, para birimi ve sistem hesapları düzenlenebilir, kod değişikliği alt hesaplara da uygulanır. Üretimde bu alanlar kilitlidir.'
              )}
            </p>
          )}
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          {/* Only the fields scroll; the header and the buttons stay put. */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            {/* Account Code */}
            <div className="space-y-2">
              <Label htmlFor="code">{t('accountModal.code', 'Account Code')} *</Label>
              <Input
                id="code"
                {...register('code')}
                placeholder={t('accountModal.codePlaceholder', 'e.g., 100.01')}
                disabled={systemLocked}
                className="font-mono"
              />
              {errors.code && (
                <p className="text-sm text-red-500">{errors.code.message}</p>
              )}
              {isEditing && unlocked && (
                <p className="text-xs text-amber-600">
                  {t(
                    'accountModal.codeCascadeWarning',
                    'Kod değişikliği alt hesaplara da uygulanır ve hesap türü / üst hesap yeni koddan yeniden türetilir. Raporlar ve otomatik muhasebe kodun ön ekine bakar.'
                  )}
                </p>
              )}
              {isEditing && !unlocked && account?.isSystemAccount !== true && (
                <p className="text-xs text-amber-600">
                  {t(
                    'accountModal.codeChangeWarning',
                    'Changing the code will re-derive the account type from the new prefix. Not allowed on accounts that have child accounts.'
                  )}
                </p>
              )}
              {isEditing && systemLocked && (
                <p className="text-xs text-muted-foreground">
                  {t('accountModal.codeCannotChangeSystem', 'System account code cannot be changed')}
                </p>
              )}
              {!isEditing && (
                <p className="text-xs text-muted-foreground">
                  {t('accountModal.codeHelp', 'Use TDMS format: 1xx = Current Assets, 2xx = Fixed Assets, 3xx = Short-term Liabilities, etc.')}
                </p>
              )}
            </div>

            {/* Account Name */}
            <div className="space-y-2">
              <Label htmlFor="name">{t('accountModal.name', 'Account Name')} *</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder={t('accountModal.namePlaceholder', 'e.g., Kasa')}
                disabled={systemLocked}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* Balance Type (TDMS B/A/C) */}
            <div className="space-y-2">
              <Label>{t('accountModal.balanceType', 'Balance Type (B/A/C)')} *</Label>
              <Combobox
                options={BALANCE_TYPES.map((type) => ({
                  value: type.value,
                  label: type.label,
                }))}
                value={balanceType}
                onValueChange={(value) => setValue('balanceType', value as TdmsBalanceType)}
                disabled={systemLocked}
                placeholder={t('accountModal.selectBalanceType', 'Select balance type')}
                searchPlaceholder={t('common.search', 'Search...')}
              />
              <p className="text-xs text-muted-foreground">
                {t('accountModal.balanceTypeHelp', 'B = Debit normal, A = Credit normal, C = Can be either')}
              </p>
            </div>

            {/* Currency */}
            <div className="space-y-2">
              <Label>{t('accountModal.currency', 'Currency')}</Label>
              <Combobox
                options={[
                  { value: 'TRY', label: 'TRY - Turk Lirasi' },
                  { value: 'USD', label: 'USD - US Dollar' },
                  { value: 'EUR', label: 'EUR - Euro' },
                  { value: 'GBP', label: 'GBP - British Pound' },
                ]}
                value={watch('currency')}
                onValueChange={(value) => setValue('currency', value)}
                disabled={(isEditing && !unlocked) || systemLocked}
                placeholder={t('accountModal.selectCurrency', 'Select currency')}
                searchPlaceholder={t('common.search', 'Search...')}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t('accountModal.description', 'Description')}</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder={t('accountModal.descriptionPlaceholder', 'Optional description for this account...')}
                rows={2}
                disabled={systemLocked}
              />
            </div>

            {/* System Account Warning */}
            {account?.isSystemAccount === true && (
              <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-700">
                {unlocked
                  ? t(
                      'accountModal.systemAccountPrepWarning',
                      'Bu bir sistem hesabı. Hazırlık modunda düzenlenebilir; üretimde yalnızca aktif/pasif durumu değiştirilebilir.'
                    )
                  : t('accountModal.systemAccountWarning', 'This is a system account. Only the name can be modified.')}
              </div>
            )}

            {/* What the change moved — reported by the server, never blocking */}
            {warnings.length > 0 && (
              <div className="space-y-1 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                <p className="font-medium">
                  {t('accountModal.impactTitle', 'Bu değişiklik neyi etkiledi?')}
                </p>
                <ul className="list-disc space-y-1 pl-4">
                  {warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Error Messages */}
            {(createMutation.error || updateMutation.error) && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                {createMutation.error?.message || updateMutation.error?.message}
              </div>
            )}

          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            {warnings.length > 0 ? (
              <Button type="button" onClick={() => onOpenChange(false)}>
                {t('accountModal.acknowledge', 'Anladım')}
              </Button>
            ) : (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? t('accountModal.saveChanges', 'Save Changes') : t('accountModal.createAccount', 'Create Account')}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
